import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { Layout } from "react-grid-layout";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { andonApi } from "~/redux/warehouse/andon";
import { workstationApi } from "~/redux/warehouse/workstation";

import { SystemMode } from "~/types/api";

import {
  AndonViewOption,
  WorkstationPongData,
  AndonStatus,
  PongData,
  AndonEventWithMeta
} from "./andon.types";

const MAX_HISTORY_ENTRIES = 1000;

export interface AndonState {
  portStatusById: Record<string, AndonStatus>;
  handRaisedByWorkstationId: Record<
    string,
    {
      raised: boolean;
      timestamp: string | null;
    }
  >;
  portIsOpenById: Record<string, boolean>;
  portHasBinsById: Record<string, number[]>;
  gridNameById: Record<string, string>;
  gridSystemModeById: Record<string, SystemMode | null>;
  currentView: AndonViewOption;
  compactViewIds: string[];
  boardViewIds: string[];
  eventExplorerIds: string[];
  boardLayout: Layout[];
  boardDimensions: {
    columns: number;
    rows: number;
  };
  editModeEnabled: boolean;
  selectedAndonLayoutName: string | null;
  eventHistory: AndonEventWithMeta[];
  eventSaveLimit: number;
  fetchWorkstationsInterval: number;
  workstationsPongs: Record<string, WorkstationPongData>;
  workstationPongHistory: Record<
    string,
    {
      pongData: PongData;
      loopTime: number;
      timestamp: string;
    }[]
  >;
  autoPingInterval: number;
  autoPingEnabled: boolean;
  pongHistoryTimeWindow: number;
}

export const initialState: AndonState = {
  portStatusById: {},
  handRaisedByWorkstationId: {},
  portIsOpenById: {},
  portHasBinsById: {},
  gridNameById: {},
  gridSystemModeById: {},
  currentView: "default",
  compactViewIds: [],
  boardViewIds: [],
  boardLayout: [],
  eventExplorerIds: [],
  boardDimensions: {
    columns: 5,
    rows: 3
  },
  editModeEnabled: false,
  eventSaveLimit: 0,
  fetchWorkstationsInterval: 0,
  workstationsPongs: {},
  workstationPongHistory: {},
  autoPingInterval: 30000,
  pongHistoryTimeWindow: 10 * 1000, // default: 10 seconds
  autoPingEnabled: false,
  eventHistory: [],
  selectedAndonLayoutName: null
};

export const andonSlice = createSlice({
  name: "andon",
  initialState,
  reducers: {
    setAndonViewOption(
      state: AndonState,
      action: PayloadAction<AndonViewOption>
    ) {
      state.currentView = action.payload;
    },
    setCompactViewIds(state: AndonState, action: PayloadAction<string[]>) {
      state.compactViewIds = action.payload;
    },
    setBoardViewIds(state: AndonState, action: PayloadAction<string[]>) {
      state.boardViewIds = action.payload;
    },
    setSelectedAndonLayoutName(
      state: AndonState,
      action: PayloadAction<string | null>
    ) {
      state.selectedAndonLayoutName = action.payload;
    },
    setEventExplorerIds(state: AndonState, action: PayloadAction<string[]>) {
      state.eventExplorerIds = action.payload;
    },
    setBoardLayout(state: AndonState, action: PayloadAction<Layout[]>) {
      state.boardLayout = action.payload;
    },
    setBoardDimensions(
      state: AndonState,
      action: PayloadAction<{ columns: number; rows: number }>
    ) {
      state.boardDimensions = action.payload;
    },
    setEditModeEnabled(state: AndonState, action: PayloadAction<boolean>) {
      state.editModeEnabled = action.payload;
    },
    setPortStatus(
      state,
      action: PayloadAction<{ portId: string; status: AndonStatus }>
    ) {
      const { portId, status } = action.payload;
      state.portStatusById[portId] = status;
    },
    setPortIsOpen(
      state,
      action: PayloadAction<{ portId: string; isOpen: boolean }>
    ) {
      const { portId, isOpen } = action.payload;
      state.portIsOpenById[portId] = isOpen;
    },
    setPortHasBins(
      state,
      action: PayloadAction<{ portId: string; bins: number[] }>
    ) {
      const { portId, bins } = action.payload;
      state.portHasBinsById[portId] = bins;
    },
    clearAndonWorkstationsAndGrids(state: AndonState) {
      state.portStatusById = {};
      state.handRaisedByWorkstationId = {};
      state.portIsOpenById = {};
      state.portHasBinsById = {};
      state.gridSystemModeById = {};
    },
    saveEventLimitData(state: AndonState, action: PayloadAction<number>) {
      state.eventSaveLimit = action.payload;
    },
    setFetchWorkstationsInterval(
      state: AndonState,
      action: PayloadAction<number>
    ) {
      state.fetchWorkstationsInterval = action.payload;
    },
    setPongHistoryTimeWindow(state: AndonState, action: PayloadAction<number>) {
      state.pongHistoryTimeWindow = action.payload;
    },
    setAutoPingInterval(state: AndonState, action: PayloadAction<number>) {
      state.autoPingInterval = action.payload;
    },
    setAutoPingEnabled(state: AndonState, action: PayloadAction<boolean>) {
      state.autoPingEnabled = action.payload;
    },
    clearPongHistory(state: AndonState) {
      state.workstationPongHistory = {};
      state.workstationsPongs = {};
    },
    setHandRaised(
      state: AndonState,
      action: PayloadAction<{
        workstationId: string;
        handRaised: boolean;
        timestamp?: string;
      }>
    ) {
      const { workstationId, handRaised } = action.payload;
      state.handRaisedByWorkstationId[workstationId] = {
        raised: handRaised,
        timestamp: action.payload.timestamp ?? null
      };
    },
    setGridSystemMode(
      state: AndonState,
      action: PayloadAction<{ gridId: string; systemMode: SystemMode }>
    ) {
      const { gridId, systemMode } = action.payload;
      state.gridSystemModeById[gridId] = systemMode;
    },
    setWorkstationPong(
      state: AndonState,
      action: PayloadAction<{
        workstationId: string;
        pongData: PongData;
        pongLoopTime: number;
      }>
    ) {
      state.workstationsPongs[action.payload.workstationId] = {
        pongData: action.payload.pongData,
        pongLoopTime: action.payload.pongLoopTime
      };
    },
    addPongHistoryEntry: (
      state,
      action: PayloadAction<{
        workstationId: string;
        pongData: PongData;
        loopTime: number;
      }>
    ) => {
      const { workstationId, pongData, loopTime } = action.payload;
      const existing = state.workstationPongHistory[workstationId] ?? [];

      const updated = [
        ...existing,
        {
          pongData,
          loopTime,
          timestamp: new Date().toISOString()
        }
      ].slice(-MAX_HISTORY_ENTRIES);

      state.workstationPongHistory[workstationId] = updated;
    },
    addEventToHistory: (state, action: PayloadAction<AndonEventWithMeta>) => {
      if (state.eventSaveLimit === 0) return;

      if (state.eventHistory.length >= state.eventSaveLimit) {
        state.eventHistory.splice(0, 1);
      }

      state.eventHistory.push(action.payload);
    },
    clearEventData(state: AndonState) {
      state.eventHistory = [];
    }
  },
  selectors: {
    selectPortStatusByIdMap: (state: AndonState) => state.portStatusById,
    selectPortOpenByIdMap: (state: AndonState) => state.portIsOpenById,
    selectPortBinsByIdMap: (state: AndonState) => state.portHasBinsById,
    selectGridNameByIdMap: (state: AndonState) => state.gridNameById,
    selectGridModeByIdMap: (state: AndonState) => state.gridSystemModeById,
    selectHandRaisedByIdMap: (state: AndonState) =>
      state.handRaisedByWorkstationId,
    selectAndonEventSaveLimit: (state: AndonState) => state.eventSaveLimit,
    selectSelectedAndonLayoutName: (state: AndonState) =>
      state.selectedAndonLayoutName,
    selectEventExplorerIds: (state: AndonState) => state.eventExplorerIds,
    selectAndonBoardViewsIds: (state: AndonState) => state.boardViewIds,
    selectAndonCompactViewsIds: (state: AndonState) => state.compactViewIds,
    selectAndonBoardLayout: (state: AndonState) => state.boardLayout,
    selectAndonBoardDimensions: (state: AndonState) => state.boardDimensions,
    selectCurrentAndonBoardView: (state: AndonState) => state.currentView,
    selectEditModeEnabled: (state: AndonState) => state.editModeEnabled,
    selectFetchWorkstationsInterval: (state: AndonState) =>
      state.fetchWorkstationsInterval,
    selectIsAndonBoardFullScreen: (state: AndonState) =>
      ["compact fullscreen", "board fullscreen"].includes(state.currentView),
    selectPongs: (state: AndonState) => state.workstationsPongs,
    selectAllPongHistories: (state: AndonState) => state.workstationPongHistory,
    selectPongHistoryTimeWindow: (state: AndonState) =>
      state.pongHistoryTimeWindow,
    selectAutoPingInterval: (state: AndonState) => state.autoPingInterval,
    selectAutoPingEnabled: (state: AndonState) => state.autoPingEnabled
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      workstationApi.endpoints.getWorkstations.matchFulfilled,
      (state, { payload }) => {
        // fresh id arrays
        const incomingWsIds = new Set<string>();
        const incomingPortIds = new Set<string>();

        // for each workstation
        for (const fetchedWorkstation of payload) {
          // add the id to the set
          incomingWsIds.add(fetchedWorkstation.id);

          // add port ids for all ports in all workstations
          fetchedWorkstation.ports.forEach((p) =>
            incomingPortIds.add(p.portId.toString())
          );

          // assign grid name by id (immer prevents mutation if name has not changed)
          state.gridNameById[fetchedWorkstation.autostoreGridId] =
            fetchedWorkstation.autostoreGridName;
        }

        const activeGridIds = new Set(payload.map((ws) => ws.autostoreGridId));

        for (const gridId of activeGridIds) {
          state.gridSystemModeById[gridId] ??= null;
        }

        // create keys for new ports if they don't exist
        for (const portId of incomingPortIds) {
          state.portHasBinsById[portId] ??= [];
          state.portIsOpenById[portId] ??= false;
        }

        // create keys for new workstations if they don't exist
        for (const wsId of incomingWsIds) {
          state.handRaisedByWorkstationId[wsId] ??= {
            raised: false,
            timestamp: null
          };
        }
      }
    );
    builder.addMatcher(
      andonApi.endpoints.getAndonState.matchFulfilled,
      (state, { payload }) => {
        for (const ws of payload.workstations) {
          state.handRaisedByWorkstationId[ws.workstationId] = {
            raised: ws.handRaised,
            timestamp: ws.handRaised ? ws.lastModified : null
          };
        }
      }
    );
  }
});

export const {
  setPortStatus,
  setPortIsOpen,
  setPortHasBins,
  setAndonViewOption,
  setCompactViewIds,
  setBoardViewIds,
  setEventExplorerIds,
  setBoardLayout,
  setBoardDimensions,
  setEditModeEnabled,
  clearAndonWorkstationsAndGrids,
  saveEventLimitData,
  setFetchWorkstationsInterval,
  setHandRaised,
  clearEventData,
  setWorkstationPong,
  addPongHistoryEntry,
  setAutoPingInterval,
  setPongHistoryTimeWindow,
  setAutoPingEnabled,
  clearPongHistory,
  setGridSystemMode,
  addEventToHistory,
  setSelectedAndonLayoutName
} = andonSlice.actions;

export const {
  selectPortStatusByIdMap,
  selectHandRaisedByIdMap,
  selectPortOpenByIdMap,
  selectPortBinsByIdMap,
  selectGridNameByIdMap,
  selectGridModeByIdMap,
  selectAndonEventSaveLimit,
  selectEventExplorerIds,
  selectAndonBoardViewsIds,
  selectAndonCompactViewsIds,
  selectAndonBoardLayout,
  selectAndonBoardDimensions,
  selectCurrentAndonBoardView,
  selectEditModeEnabled,
  selectFetchWorkstationsInterval,
  selectIsAndonBoardFullScreen,
  selectPongs,
  selectAllPongHistories,
  selectAutoPingInterval,
  selectPongHistoryTimeWindow,
  selectAutoPingEnabled,
  selectSelectedAndonLayoutName
} = andonSlice.selectors;

export const selectPortStatus = (portId: string) =>
  createSelector(
    (state: { andon: AndonState }) => state.andon.portStatusById,
    (map: Record<string, AndonStatus>): AndonStatus => map[portId] ?? "good"
  );

export const selectPongLoopHistory = (workstationId: string) =>
  createSelector(
    (state: { andon: AndonState }) =>
      state.andon.workstationPongHistory[workstationId],
    (
      entries:
        | {
            pongData: PongData;
            loopTime: number;
            timestamp: string;
          }[]
        | undefined
    ) => [...(entries ?? [])]
  );

const andonPersistConfig = {
  key: "andon",
  storage,
  whitelist: [
    "currentView",
    "compactViewIds",
    "boardViewIds",
    "eventExplorerIds",
    "boardLayout",
    "boardDimensions",
    "editModeEnabled",
    "fetchWorkstationsInterval",
    "workstationPongs",
    "workstationPongHistory",
    "autoPingInterval",
    "pongHistoryTimeWindow",
    "autoPingEnabled",
    "portStatusById",
    "handRaisedByWorkstationId",
    "selectedAndonLayoutName",
    "eventHistory",
    "eventSaveLimit"
  ]
};

export const andonReducer = persistReducer(
  andonPersistConfig,
  andonSlice.reducer
);
